/**
 * Fixture data for Recap stories + unit tests. Realistic-looking
 * sniper-vs-brawler engagement, ~6 turns, with the kind of reasoning
 * blurbs the LLM agents actually emit.
 */

import type { RecapMatch, RecapTournament } from "./types.js";

export const FIXTURE_MATCH: RecapMatch = {
  id: "brawler_vs_sniper",
  bestOf: 3,
  startedAt: "2026-05-15T14:23:00Z",
  winsA: 2,
  winsB: 1,
  winner: "A",
  reason: "A wins 2-1",
  agentA: {
    side: "A",
    name: "brawler_v3",
    model: "protolabs/fast",
    archetype: "brawler",
    chassis: "Centurion",
    finalHp: 22,
    winner: true,
    loadout: [
      { name: "AC20", label: "AC/20" },
      { name: "MEDIUM_LASER", label: "Medium Laser" },
      { name: "MEDIUM_LASER", label: "Medium Laser" },
      { name: "JUMP_JETS", label: "Jump Jets" },
      { name: "SMALL_LASER", label: "Small Laser" },
    ],
  },
  agentB: {
    side: "B",
    name: "sniper_v3",
    model: "protolabs/fast",
    archetype: "sniper",
    chassis: "Catapult",
    finalHp: 0,
    winner: false,
    loadout: [
      { name: "PPC", label: "PPC" },
      { name: "GAUSS", label: "Gauss Rifle", ammo: 6 },
      { name: "MEDIUM_LASER", label: "Medium Laser" },
      { name: "SMALL_LASER", label: "Small Laser" },
    ],
  },
  battles: [
    {
      index: 1,
      seed: 43,
      map: "arena",
      finalTurn: 7,
      winner: "A",
      reason: "KILLSHOT",
      entries: [
        {
          id: "b1-deploy-a",
          side: "A",
          turn: 0,
          phase: "deploy",
          reasoningText:
            "As a brawler I need to close distance fast. Forward-most hex (-3, -2) minimises turns in the sniper's long-range lane and puts me on the fastest path to the opponent.",
          actionLines: ["DEPLOY ⟶ (-3, -2) FACING SE"],
        },
        {
          id: "b1-deploy-b",
          side: "B",
          turn: 0,
          phase: "deploy",
          reasoningText:
            "Sniper on 'arena' — back-edge hex (3, 2) to maximise opening engagement distance. PPC + GAUSS punish forward movement; brawlers have to cross open ground.",
          actionLines: ["DEPLOY ⟶ (3, 2) FACING SW"],
        },
        {
          id: "b1-t1-a",
          side: "A",
          turn: 1,
          phase: "decide",
          reasoningText:
            "Sniper sits at extreme range. I burn movement points to gain a hex of cover and close — no shots at this range are worth the heat. Jump-jet hop puts me behind the central ridge.",
          actionLines: ["JUMP ⟶ (-1, -1) FACING E", "VENT HEAT"],
          outcomes: [{ severity: "info", text: "Closed 2 hex · gained ridge cover", affects: "A" }],
          stats: { model: "protolabs/fast", tokensOut: 142, latencyMs: 1620 },
        },
        {
          id: "b1-t1-b",
          side: "B",
          turn: 1,
          phase: "decide",
          reasoningText:
            "Brawler took cover. Direct fire blocked by terrain. Pre-aim PPC arc on the only viable exit hex and gauss-charge for next turn — burst-window in T2.",
          actionLines: ["AIM PPC ⟶ EXIT HEX (0, -1)", "GAUSS CHARGE"],
          outcomes: [{ severity: "info", text: "PPC pre-aimed · gauss charging", affects: "B" }],
          stats: { model: "protolabs/fast", tokensOut: 168, latencyMs: 1990 },
        },
        {
          id: "b1-t2-a",
          side: "A",
          turn: 2,
          phase: "decide",
          reasoningText:
            "If I exit predictably I eat the PPC + gauss alpha. Fake the exit one hex, jump-jet over the ridge instead. Hot but worth the surprise.",
          actionLines: ["JUMP ⟶ (1, 0) FACING NE"],
          outcomes: [
            { severity: "info", text: "Heat 38/100 — yellow band", affects: "A" },
            { severity: "warn", text: "PPC pre-aim wasted · sniper recovers next turn", affects: "B" },
          ],
          stats: { model: "protolabs/fast", tokensOut: 134, latencyMs: 1480 },
        },
        {
          id: "b1-t2-b",
          side: "B",
          turn: 2,
          phase: "decide",
          reasoningText:
            "Pre-aim missed. Brawler is in mid-range and closing. Best play: fire gauss now into front_ct before AC/20 enters its band. PPC is too hot to follow up.",
          actionLines: ["FIRE GAUSS ⟶ A · FRONT_CT"],
          outcomes: [
            { severity: "alert", text: "GAUSS HIT · front_ct armor 14 → 2", affects: "A" },
            { severity: "info", text: "Gauss ammo 6 → 5", affects: "B" },
          ],
          stats: { model: "protolabs/fast", tokensOut: 121, latencyMs: 1330 },
        },
        {
          id: "b1-t3-a",
          side: "A",
          turn: 3,
          phase: "decide",
          reasoningText:
            "Front_ct critical. One more gauss and I'm exposed. Need to close to AC/20 range THIS turn or I'm dead in two. Walk straight in — accept the next hit, trade alpha for alpha.",
          actionLines: ["WALK ⟶ (2, 1) FACING SE", "FIRE MEDIUM LASER ⟶ B · L_SIDE"],
          outcomes: [
            { severity: "info", text: "Range 2 — AC/20 in band next turn", affects: "A" },
            { severity: "warn", text: "Medium Laser hit · B l_side armor 6 → 3", affects: "B" },
          ],
          stats: { model: "protolabs/fast", tokensOut: 156, latencyMs: 1720 },
        },
        {
          id: "b1-t3-b",
          side: "B",
          turn: 3,
          phase: "decide",
          reasoningText:
            "AC/20 in band next turn. I get one more shot. Stack everything into front_ct — already exposed at armor 2. PPC + gauss alpha now or never.",
          actionLines: ["FIRE PPC ⟶ A · FRONT_CT", "FIRE GAUSS ⟶ A · FRONT_CT"],
          outcomes: [
            { severity: "alert", text: "PPC HIT · front_ct armor 2 → 0 · internal exposed", affects: "A" },
            { severity: "alert", text: "GAUSS HIT · front_ct internal 6 → 0 · breach!", affects: "A" },
            { severity: "warn", text: "B heat 71/100 — shutdown imminent", affects: "B" },
          ],
          stats: { model: "protolabs/fast", tokensOut: 188, latencyMs: 2110 },
        },
        {
          id: "b1-t4-a",
          side: "A",
          turn: 4,
          phase: "decide",
          reasoningText:
            "Front_ct breached but I'm still mobile. B is one shutdown away from giving me a free turn. Close to point-blank, dump AC/20 into rear_ct — if B vents I get a free killshot.",
          actionLines: ["WALK ⟶ (3, 2) FACING N", "FIRE AC20 ⟶ B · REAR_CT"],
          outcomes: [
            { severity: "alert", text: "AC/20 HIT · B rear_ct armor 4 → 0 · internal 6 → 0", affects: "B" },
            { severity: "alert", text: "B KILLSHOT · rear_ct breach + center engine", affects: "B" },
          ],
          stats: { model: "protolabs/fast", tokensOut: 173, latencyMs: 1880 },
        },
        {
          id: "b1-debrief-a",
          side: "A",
          turn: 4,
          phase: "debrief",
          reasoningText:
            "Trade-alpha-for-alpha worked. AC/20 closer pays for the front_ct breach because B couldn't survive shutdown pressure + close-range follow-up.",
        },
      ],
    },
    {
      index: 2,
      seed: 44,
      map: "arena",
      finalTurn: 9,
      winner: "B",
      reason: "SHUTDOWN+DOT",
      entries: [
        {
          id: "b2-t1-a",
          side: "A",
          turn: 1,
          phase: "decide",
          reasoningText:
            "Different opening — B will adapt to the jump-jet rush. Try a flanking approach via the left ridge instead. Slower but safer.",
          actionLines: ["WALK ⟶ (-2, -2) FACING E"],
        },
        {
          id: "b2-t1-b",
          side: "B",
          turn: 1,
          phase: "decide",
          reasoningText:
            "Brawler flanking left. Reposition for cross-fire angle, keep gauss charged, force them to commit before AC/20 band.",
          actionLines: ["WALK ⟶ (2, 1) FACING W", "GAUSS CHARGE"],
        },
      ],
    },
    {
      index: 3,
      seed: 45,
      map: "highlands",
      finalTurn: 6,
      winner: "A",
      reason: "KILLSHOT",
      entries: [
        {
          id: "b3-t1-a",
          side: "A",
          turn: 1,
          phase: "decide",
          reasoningText:
            "Highlands map — elevation matters. High-ground sniper play is harder, brawler has terrain to exploit on the approach.",
          actionLines: ["WALK ⟶ (-2, -1) FACING NE"],
        },
      ],
    },
  ],
};

export const FIXTURE_TOURNAMENT: RecapTournament = {
  id: "iter-21-archetype-rr",
  name: "Iter-21 Archetype Round Robin",
  description: "5 archetypes · best-of-3 · 10 matches · 2026-05-15",
  startedAt: "2026-05-15T14:00:00Z",
  matches: [
    FIXTURE_MATCH,
    {
      ...FIXTURE_MATCH,
      id: "sniper_vs_scout",
      startedAt: "2026-05-15T14:48:00Z",
      winsA: 1,
      winsB: 2,
      winner: "B",
      reason: "B wins 2-1",
      agentA: { ...FIXTURE_MATCH.agentA, name: "sniper_v3", archetype: "sniper", chassis: "Catapult", winner: false },
      agentB: { ...FIXTURE_MATCH.agentB, name: "scout_v2", archetype: "scout", chassis: "Wasp", winner: true },
    },
    {
      ...FIXTURE_MATCH,
      id: "brawler_vs_generalist",
      startedAt: "2026-05-15T15:12:00Z",
      winsA: 2,
      winsB: 0,
      winner: "A",
      reason: "A wins 2-0",
      agentA: { ...FIXTURE_MATCH.agentA, name: "brawler_v3" },
      agentB: { ...FIXTURE_MATCH.agentB, name: "generalist_v2", archetype: "generalist", chassis: "Warhammer" },
    },
    {
      ...FIXTURE_MATCH,
      id: "scout_vs_brawler",
      startedAt: "2026-05-15T15:36:00Z",
      winsA: 0,
      winsB: 2,
      winner: "B",
      reason: "B wins 2-0",
      agentA: { ...FIXTURE_MATCH.agentA, name: "scout_v2", archetype: "scout", chassis: "Wasp", winner: false },
      agentB: { ...FIXTURE_MATCH.agentB, name: "brawler_v3", archetype: "brawler", chassis: "Centurion", winner: true },
    },
  ],
};

export const FIXTURE_TOURNAMENTS: ReadonlyArray<RecapTournament> = [
  FIXTURE_TOURNAMENT,
  {
    id: "iter-20-archetype-rr",
    name: "Iter-20 Archetype Round Robin",
    description: "Pre-perception-fix baseline",
    startedAt: "2026-05-13T18:00:00Z",
    matches: FIXTURE_TOURNAMENT.matches.slice(0, 2),
  },
  {
    id: "canary-rr-trial",
    name: "Canary RR Trial",
    description: "Smoke test of round-robin harness changes",
    startedAt: "2026-05-09T11:30:00Z",
    matches: [FIXTURE_TOURNAMENT.matches[0]],
  },
];
